// useVFXConfig Hook
// Custom hook to use VFX config in components

import { useMemo } from 'react';
import { useVFXConfigContext } from '../contexts/VFXConfigContext';
import type { VFXConfig, BackgroundConfig } from '../types/vfxConfig';

/**
 * Hook to access VFX configuration
 * Returns VFX config, background config, and helper functions
 */
export function useVFXConfig() {
  const { state, refreshConfig } = useVFXConfigContext();

  const vfxConfig = useMemo<VFXConfig | null>(() => {
    return state.config?.vfx || null;
  }, [state.config]);

  const backgroundConfig = useMemo<BackgroundConfig | null>(() => {
    const bgConfig = state.config?.background || null;
    return bgConfig;
  }, [state.config]);

  const isVFXEnabled = useMemo(() => {
    return state.config?.vfx?.enabled ?? false;
  }, [state.config]);

  const isBackgroundEnabled = useMemo(() => {
    return state.config?.background?.enabled ?? false;
  }, [state.config]);

  const configVersion = useMemo(() => {
    return state.config?.version || null;
  }, [state.config]);

  const lastUpdated = useMemo(() => {
    return state.config?.updatedAt || null;
  }, [state.config]);

  const updatedBy = useMemo(() => {
    return state.config?.updatedBy || null;
  }, [state.config]);

  return {
    // Config data
    vfxConfig,
    backgroundConfig,

    // State flags
    isVFXEnabled,
    isBackgroundEnabled,
    isLoading: state.isLoading,
    error: state.error,

    // Metadata
    configVersion,
    lastUpdated,
    updatedBy,
    lastFetched: state.lastFetched,

    // Actions
    refreshConfig,
  };
}

